import { startOfDay, endOfDay, startOfWeek, endOfWeek, subDays, subWeeks } from 'date-fns';
import type { TripRoutePoint } from '../types/trip';

// FE-26 — presets de rango para el histórico de recorrido (FE-25). Mismo
// criterio de semana que `CalendarPage.tsx`: empieza en lunes.
export type HistoryPreset = 'today' | 'yesterday' | 'week' | 'lastWeek';

export const HISTORY_PRESETS: { id: HistoryPreset; label: string }[] = [
  { id: 'today', label: 'Hoy' },
  { id: 'yesterday', label: 'Ayer' },
  { id: 'week', label: 'Esta semana' },
  { id: 'lastWeek', label: 'Semana pasada' },
];

export function presetRange(preset: HistoryPreset, now = new Date()): { from: Date; to: Date } {
  switch (preset) {
    case 'today':
      return { from: startOfDay(now), to: now };
    case 'yesterday': {
      const y = subDays(now, 1);
      return { from: startOfDay(y), to: endOfDay(y) };
    }
    case 'week':
      return { from: startOfWeek(now, { weekStartsOn: 1 }), to: now };
    case 'lastWeek': {
      const lw = subWeeks(now, 1);
      return { from: startOfWeek(lw, { weekStartsOn: 1 }), to: endOfWeek(lw, { weekStartsOn: 1 }) };
    }
  }
}

// ---------------------------------------------------------------------------
// Reproducción animada (FE-26).
//
// El contrato no trae `heading` por punto del histórico (sí lo trae la
// posición EN VIVO, `VehiclePosition.heading` — pero eso es otro dato): se
// aproxima con el rumbo (bearing) entre los dos puntos que rodean el instante
// actual. Tampoco trae velocidad interpolada — se usa la del punto anterior,
// que es la última medida real conocida en ese instante.
// ---------------------------------------------------------------------------

export interface InterpolatedPosition {
  lat: number;
  lng: number;
  bearing: number;
  speedKmh?: number;
  occurredAt: string;
}

// 🚨 Hallazgo real (2026-09-18, dataset de PAT001): entre dos puntos
// consecutivos puede haber saltos de horas o días —el equipo dejó de
// reportar (apagado, sin señal, fuera del rango consultado)— y unirlos con
// una línea recta cruza el mapa por donde el vehículo nunca estuvo: se
// midió un salto real de 24 km tras 87 horas sin datos. 10 minutos es
// bastante más que el intervalo normal de reporte (10–30s en movimiento,
// hasta un par de minutos detenido) y bastante menos que un hueco real.
const MAX_SEGMENT_GAP_MS = 10 * 60 * 1000;

/**
 * Corta la polyline en tramos: uno nuevo cada vez que el hueco hasta el
 * punto siguiente supera `MAX_SEGMENT_GAP_MS`. Cada tramo se dibuja como su
 * propia `Polyline` — no se conectan entre sí.
 */
export function splitRouteIntoSegments(points: TripRoutePoint[]): TripRoutePoint[][] {
  if (points.length === 0) return [];
  const segments: TripRoutePoint[][] = [[points[0]]];
  for (let i = 1; i < points.length; i++) {
    const gapMs = Date.parse(points[i].occurredAt) - Date.parse(points[i - 1].occurredAt);
    if (gapMs > MAX_SEGMENT_GAP_MS) {
      segments.push([points[i]]);
    } else {
      segments[segments.length - 1].push(points[i]);
    }
  }
  return segments;
}

// ---------------------------------------------------------------------------
// Trazo del recorrido: medido vs. estimado (FE-27, seguimiento 2026-09-20).
//
// Hallazgo del dueño: hay partes donde la línea sale recta. Medido con el
// fixture real de Jimi (1 día, 1.126 puntos, `docs/exploracion-openapi/
// fixtures/10_*`): el dato CRUDO sale cada ~10 s y su salto máximo es de 205 m,
// así que ahí no hay rectas visibles. Las produce (a) el adelgazado uniforme
// del servidor —1 de 12 → cuerdas de 2 km— y (b) cualquier hueco de hasta
// `MAX_SEGMENT_GAP_MS` (10 min ≈ 13 km a 80 km/h), que se une con una recta.
//
// No se puede inventar la curva que falta (eso es map-matching, decisión
// aparte), pero sí decir la verdad: un tramo donde el equipo no reportó en 2 min
// y que ya cubre una distancia visible se dibuja PUNTEADO ("estimado"); el
// resto, sólido ("medido"). Un vehículo parado con reportes espaciados no
// dibuja nada que distinguir (distancia ~0), por eso también se exige distancia.
// ---------------------------------------------------------------------------

const ESTIMATED_HOP_MIN_GAP_MS = 2 * 60 * 1000;
const ESTIMATED_HOP_MIN_DISTANCE_M = 200;

/** ¿El tramo a→b es una recta "estimada"? (más de 2 min sin reportar Y más de 200 m de cuerda). */
export function isEstimatedHop(a: TripRoutePoint, b: TripRoutePoint): boolean {
  const gapMs = Date.parse(b.occurredAt) - Date.parse(a.occurredAt);
  return gapMs > ESTIMATED_HOP_MIN_GAP_MS && haversineMeters(a, b) > ESTIMATED_HOP_MIN_DISTANCE_M;
}

/** Una porción continua de la polyline con un mismo estilo (sólida o punteada). */
export interface TraceRun {
  positions: [number, number][];
  estimated: boolean;
}

/**
 * Índice del último punto con `times[i] <= ms` (búsqueda binaria; `times` va
 * ordenado, lo promete el contrato). `-1` si `ms` es anterior al primero.
 */
export function findLastIndexAtOrBefore(times: number[], ms: number): number {
  let lo = 0;
  let hi = times.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (times[mid] <= ms) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found;
}

/**
 * Los tramos ya recorridos —`points[0..lastIdx]`— agrupados en corridas del
 * mismo estilo. Un hueco de más de `MAX_SEGMENT_GAP_MS` NO se dibuja (corta la
 * corrida). Toda corrida trae al menos 2 vértices: nace de un tramo (dos
 * puntos), así que nunca produce la polyline de un solo vértice que rompe el
 * *renderer* SVG de Leaflet (ver `RouteHistoryLayers`).
 *
 * Es una función de `lastIdx`, no del instante exacto: cambia sólo cuando la
 * reproducción PASA un punto real, no en cada tick. Así la `Polyline` conserva
 * su identidad entre ticks y Leaflet no re-proyecta miles de vértices cada
 * 50 ms (ver `TraceRun` en `RouteHistoryLayers`).
 */
export function buildTraceRuns(points: TripRoutePoint[], lastIdx: number): TraceRun[] {
  const runs: TraceRun[] = [];
  const end = Math.min(lastIdx, points.length - 1);
  let current: TraceRun | null = null;

  for (let i = 1; i <= end; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (Date.parse(b.occurredAt) - Date.parse(a.occurredAt) > MAX_SEGMENT_GAP_MS) {
      current = null; // hueco: no se conecta
      continue;
    }
    const estimated = isEstimatedHop(a, b);
    if (!current || current.estimated !== estimated) {
      current = { positions: [[a.lat, a.lng]], estimated };
      runs.push(current);
    }
    current.positions.push([b.lat, b.lng]);
  }
  return runs;
}

/**
 * La punta viva: del último punto real ya pasado hasta la posición interpolada
 * actual. Cambia en cada tick, pero son sólo 2 vértices. `null` si el vehículo
 * está justo sobre un punto, o si el tramo siguiente es un hueco (ahí el marcador
 * espera quieto en p0 — ver `interpolateRoutePosition`).
 */
export function buildTraceTip(
  points: TripRoutePoint[],
  lastIdx: number,
  position: { lat: number; lng: number },
): TraceRun | null {
  if (lastIdx < 0 || lastIdx >= points.length - 1) return null;
  const a = points[lastIdx];
  const b = points[lastIdx + 1];
  if (Date.parse(b.occurredAt) - Date.parse(a.occurredAt) > MAX_SEGMENT_GAP_MS) return null;
  if (a.lat === position.lat && a.lng === position.lng) return null;
  return { positions: [[a.lat, a.lng], [position.lat, position.lng]], estimated: isEstimatedHop(a, b) };
}

function bearingBetween(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const toDeg = (r: number) => (r * 180) / Math.PI;
  const dLng = toRad(b.lng - a.lng);
  const y = Math.sin(dLng) * Math.cos(toRad(b.lat));
  const x = Math.cos(toRad(a.lat)) * Math.sin(toRad(b.lat)) - Math.sin(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/**
 * Puntos ordenados cronológicamente (lo promete el contrato). Interpola
 * linealmente entre los dos que rodean `atMs` — suficiente para la escala de
 * un mapa; no pretende ser una proyección geodésica exacta.
 */
export function interpolateRoutePosition(points: TripRoutePoint[], atMs: number): InterpolatedPosition | null {
  if (points.length === 0) return null;
  if (points.length === 1) {
    const p = points[0];
    return { lat: p.lat, lng: p.lng, bearing: 0, speedKmh: p.speedKmh, occurredAt: p.occurredAt };
  }

  const first = Date.parse(points[0].occurredAt);
  const last = Date.parse(points[points.length - 1].occurredAt);
  const clamped = Math.min(Math.max(atMs, first), last);

  let i = 0;
  while (i < points.length - 2 && Date.parse(points[i + 1].occurredAt) < clamped) i++;
  const p0 = points[i];
  const p1 = points[i + 1];
  const t0 = Date.parse(p0.occurredAt);
  const t1 = Date.parse(p1.occurredAt);
  // Mismo hueco que corta la polyline (`splitRouteIntoSegments`): si el
  // equipo no reportó por más de `MAX_SEGMENT_GAP_MS`, el marcador no puede
  // "deslizarse" en línea recta durante la reproducción — se queda quieto en
  // p0 y salta a p1 recién al llegar su instante real.
  const isGap = t1 - t0 > MAX_SEGMENT_GAP_MS;
  const t = isGap ? (clamped >= t1 ? 1 : 0) : (t1 > t0 ? (clamped - t0) / (t1 - t0) : 0);

  return {
    lat: p0.lat + (p1.lat - p0.lat) * t,
    lng: p0.lng + (p1.lng - p0.lng) * t,
    bearing: bearingBetween(p0, p1),
    speedKmh: t === 0 ? p0.speedKmh : p1.speedKmh,
    occurredAt: new Date(clamped).toISOString(),
  };
}

// ---------------------------------------------------------------------------
// Paradas (FE-26, ajustada en #189): agrupar tramos en los que el vehículo
// se queda confinado a un radio pequeño O reporta velocidad baja, sostenido en
// el tiempo — un semáforo en rojo no es una parada, un cliente que atender sí.
// El radio es un umbral interno para agrupar, no un dato que se muestra. (La distancia de la polyline SÍ se muestra desde FE-27 —
// `routeDistanceKm`, más abajo—, pero como aproximada: no es el odómetro.)
// ---------------------------------------------------------------------------

export interface RouteStop {
  lat: number;
  lng: number;
  arrivedAt: string;
  leftAt: string;
  durationMin: number;
}

const STOP_SPEED_THRESHOLD_KMH = 3;
const STOP_MIN_DURATION_MIN = 3;
const STOP_MAX_RADIUS_M = 80;

function haversineMeters(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

// FE-27 (2026-09-19, pedido del dueño) — distancia del recorrido consultado.
// Revoca la decisión de FE-26 de no mostrarla, con tres condiciones que la
// hacen honesta:
//   1. Es la longitud de la polyline, NO el odómetro del equipo (`FE-19`:
//      `Vehicle.odometer` es el acumulado del GPS y sigue siendo la única
//      fuente de "kilómetros del vehículo"). La UI la rotula "aprox. por GPS".
//   2. Suma por SEGMENTO continuo (`splitRouteIntoSegments`), no punto a
//      punto: entre dos lecturas separadas por más de `MAX_SEGMENT_GAP_MS` el
//      equipo estuvo mudo y unirlas sumaría una línea recta por donde el
//      vehículo nunca pasó (medido: 24 km tras 87 h sin datos).
//   3. No suma el tramo entre dos puntos AMBOS detenidos (velocidad medida
//      ≤ `STOP_SPEED_THRESHOLD_KMH`): un GPS quieto oscila unos metros, y 8 h
//      parado con ~3 m de ruido cada 30 s suman 3 km que nunca se recorrieron
//      (medido con datos sintéticos). Solo se descarta con velocidad MEDIDA: sin
//      `speedKmh` el tramo cuenta, para no dar 0 km a un equipo que no la manda.
//      Arrastrarse a ≤3 km/h también queda fuera — es una subestimación de unos
//      metros por tramo, coherente con "aproximada".
// Si el servidor adelgazó la polyline (`downsampled`), la suma sale corta —
// atajos entre puntos conservados—; quien la muestre debe avisarlo.
export function routeDistanceKm(points: TripRoutePoint[]): number {
  const isMeasuredStopped = (p: TripRoutePoint) => p.speedKmh !== undefined && p.speedKmh <= STOP_SPEED_THRESHOLD_KMH;
  let meters = 0;
  for (const segment of splitRouteIntoSegments(points)) {
    for (let i = 1; i < segment.length; i++) {
      if (isMeasuredStopped(segment[i - 1]) && isMeasuredStopped(segment[i])) continue;
      meters += haversineMeters(segment[i - 1], segment[i]);
    }
  }
  return meters / 1000;
}

/** "158,5 km" — un decimal, separador de coma. En pantalla siempre se acompaña de "aprox. por GPS". */
export function formatApproxDistanceKm(km: number): string {
  return `${km.toLocaleString('es-CO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;
}

// #189 — el equipo quieto que reporta 7 km/h. El agrupamiento ANTES arrancaba
// solo con velocidad baja (≤ 3 km/h), así que un punto con velocidad mal
// reportada (> 3 km/h aunque en el mapa el vehículo no se mueve) nunca entraba
// al grupo y el radio de 80 m ni se evaluaba: una parada real de 5 min se
// perdía. Ahora el agrupamiento es ESPACIAL: un punto sigue en el tramo si está
// dentro de 80 m del ancla (primer punto del tramo). Esto marca la parada
// aunque la velocidad mienta, y acota la extensión del tramo al radio.
//
// Deliberadamente NO se agrupa también por "velocidad ≤ 3 km/h" suelta: un
// trancón en el que el vehículo AVANZA a 2-3 km/h sostenidos (todos los puntos
// ≤ 3 km/h pero recorriendo cientos de metros) se marcaría como una parada
// falsa. Toda parada real ya cumple el radio, así que esa rama solo sumaría
// falsos positivos. STOP_SPEED_THRESHOLD_KMH sigue vivo para `routeDistanceKm`.
function isStationary(anchor: TripRoutePoint, p: TripRoutePoint): boolean {
  return haversineMeters(anchor, p) <= STOP_MAX_RADIUS_M;
}

export function detectStops(points: TripRoutePoint[]): RouteStop[] {
  const stops: RouteStop[] = [];
  let i = 0;

  while (i < points.length) {
    const anchor = points[i];
    let end = i; // último índice que sigue quieto respecto al ancla
    while (end + 1 < points.length && isStationary(anchor, points[end + 1])) end++;

    if (end > i) {
      const run = points.slice(i, end + 1);
      const durationMin = (Date.parse(run[run.length - 1].occurredAt) - Date.parse(run[0].occurredAt)) / 60000;
      if (durationMin >= STOP_MIN_DURATION_MIN) {
        stops.push({
          lat: run.reduce((s, p) => s + p.lat, 0) / run.length,
          lng: run.reduce((s, p) => s + p.lng, 0) / run.length,
          arrivedAt: run[0].occurredAt,
          leftAt: run[run.length - 1].occurredAt,
          durationMin,
        });
      }
      i = end + 1; // el punto que rompió el tramo arranca el siguiente
    } else {
      i++; // punto en movimiento, no ancla ninguna parada
    }
  }

  return stops;
}
